/**
 * Viewer-first layout snapshot — save/export/refine use the same normalized artboard state.
 */
import {
  elementLayoutKeyFromElement,
  findLogoImage,
  isLogoImage,
  normalizeArtboardDom,
  queryLogoImages,
} from './mediaCreatorDomRegistry';
import {
  clearTextBoxSize,
  isTextElement,
  LAYOUT_APPLIED_ATTR,
  parseInlineTransform,
  TEXT_SIZED_ATTR,
} from './mediaCreatorElementLayout';
import { CUTOUT_ATTR } from './mediaCreatorHtmlResolve';
import { stripZoomTransform, ZOOM_ATTR } from './mediaCreatorPhotoFit';

const EDITOR_ATTRS = [
  TEXT_SIZED_ATTR,
  LAYOUT_APPLIED_ATTR,
  CUTOUT_ATTR,
  ZOOM_ATTR,
  'contenteditable',
  'spellcheck',
  'data-mc-thumb-fallback',
  'data-mc-export-id',
  'data-mc-export-decor',
  'data-mc-export-decor-hide',
];

const PHOTO_STYLE_KEYS = ['objectFit', 'objectPosition'];

function isIdentityTransform(t) {
  return (
    !t
    || ((Number(t.tx) || 0) === 0
      && (Number(t.ty) || 0) === 0
      && (Number(t.sx) || 1) === 1
      && (Number(t.sy) || 1) === 1)
  );
}

export function normalizeDocumentCssForPersist(css) {
  if (!css) return css;
  return css.replace(/source\s+sans\s+pro/gi, 'Source Sans 3');
}

function stripEditorAttributes(root) {
  if (!root) return;
  root.querySelectorAll('*').forEach((el) => {
    EDITOR_ATTRS.forEach((name) => el.removeAttribute(name));
    if (el.hasAttribute('style') && el.style.length === 0) {
      el.removeAttribute('style');
    }
  });
}

function prunePhotoInlineStyles(img) {
  const keep = {};
  PHOTO_STYLE_KEYS.forEach((key) => {
    const v = img.style[key];
    if (v) keep[key] = v;
  });
  img.removeAttribute('style');
  Object.entries(keep).forEach(([k, v]) => {
    img.style[k] = v;
  });
}

function collectLiveLayoutFromDom(root, baseLayout) {
  const elementLayout = { ...(baseLayout?.element_layout || {}) };
  const photoPositions = { ...(baseLayout?.photo_object_positions || {}) };
  const photoBindings = { ...(baseLayout?.photo_bindings || {}) };
  let logoBinding = baseLayout?.logo_binding;

  const visit = (el) => {
    if (!(el instanceof Element)) return;
    const key = elementLayoutKeyFromElement(el);
    const transform = stripZoomTransform(el.style?.transform);
    if (key && transform) {
      const parsed = parseInlineTransform(transform);
      if (isTextElement(el)) {
        parsed.sx = 1;
        parsed.sy = 1;
      }
      if (!isIdentityTransform(parsed)) {
        elementLayout[key] = { ...(elementLayout[key] || {}), ...parsed };
        el.style.removeProperty('transform');
        el.style.removeProperty('transform-origin');
      }
    }
    clearTextBoxSize(el);
  };

  root.querySelectorAll('[data-editable]').forEach(visit);
  root.querySelectorAll('.mc-copy, .mc-gallery, .mc-invite, .mc-badges, figure.mc-photo').forEach(visit);
  queryLogoImages(root).forEach(visit);

  root.querySelectorAll('img[data-photo-slot]').forEach((img) => {
    const slot = img.getAttribute('data-photo-slot');
    if (!slot) return;
    const op = img.style.objectPosition || img.style.getPropertyValue('object-position');
    if (op) photoPositions[slot] = op;
    const assetId = img.getAttribute('data-media-asset-id');
    if (assetId) photoBindings[slot] = assetId;
    prunePhotoInlineStyles(img);
  });

  const logo = findLogoImage(root);
  if (logo) {
    const assetId = logo.getAttribute('data-media-asset-id');
    if (assetId) logoBinding = assetId;
    if (!logo.hasAttribute('data-logo-slot')) {
      logo.setAttribute('data-logo-slot', 'brand');
    }
    logo.removeAttribute('src');
    logo.removeAttribute('crossorigin');
  }

  root.querySelectorAll('img[data-photo-slot]').forEach((img) => {
    img.removeAttribute('src');
    img.removeAttribute('crossorigin');
  });

  return {
    element_layout: elementLayout,
    photo_object_positions: photoPositions,
    photo_bindings: photoBindings,
    logo_binding: logoBinding,
  };
}

/**
 * Read live artboard markup so save/export include in-progress contenteditable edits.
 */
export function readLiveDocumentHtml(artboardRoot) {
  if (!artboardRoot) return null;
  const main =
    artboardRoot.querySelector('.mc-html-host .mc-invite')
    || artboardRoot.querySelector('.mc-html-stage-inner .mc-invite')
    || artboardRoot.querySelector('.mc-invite');
  return main?.outerHTML || null;
}

/**
 * Normalize live DOM to persisted document_html + layout fields from viewer.
 */
export function captureLayoutSnapshot(artboardRoot, baseLayout) {
  if (!baseLayout || !artboardRoot) return baseLayout || {};
  const live = readLiveDocumentHtml(artboardRoot);
  if (!live) return { ...baseLayout };

  const parser = new DOMParser();
  const doc = parser.parseFromString(live, 'text/html');
  const root = doc.querySelector('.mc-invite');
  if (!root) {
    return { ...baseLayout, document_html: live };
  }

  normalizeArtboardDom(root);
  const liveFields = collectLiveLayoutFromDom(root, baseLayout);
  stripEditorAttributes(root);

  return {
    ...baseLayout,
    ...liveFields,
    document_html: root.outerHTML,
    document_css: normalizeDocumentCssForPersist(baseLayout.document_css || ''),
  };
}

export function mergeLayoutWithLiveHtml(layout, artboardRoot) {
  return captureLayoutSnapshot(artboardRoot, layout);
}

/** Fingerprint for unsaved-change hint (headline + when + logo). */
export function layoutViewerFingerprint(layout) {
  if (!layout?.document_html) return '';
  const parser = new DOMParser();
  const doc = parser.parseFromString(layout.document_html, 'text/html');
  const headline = doc.querySelector('[data-editable="headline"]')?.textContent?.trim() || '';
  const when = doc.querySelector('[data-editable="when_time"]')?.textContent?.trim() || '';
  const logo = layout.logo_binding ?? '';
  return `${headline}|${when}|${logo}`;
}
